import React from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { IconButton, Tooltip, Box, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

const SCHEDULE_TYPES = new Set(["interval", "inactivity", "awaiting_response", "awaiting_client_reply"]);

const switchSx = {
  "& .MuiSwitch-switchBase": {
    color: "error.main",
  },
  "& .MuiSwitch-track": {
    backgroundColor: "error.main",
    opacity: 0.45,
  },
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "success.main",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "success.main",
    opacity: 0.55,
  },
};

function isScheduledRule(rule) {
  return SCHEDULE_TYPES.has(String(rule?.schedule_type || "").trim());
}

function statusTooltip(t, { isOn, isPaused, isScheduled }) {
  if (isPaused) return t("automationRuleTable.tooltipPaused");
  if (isScheduled) {
    return isOn
      ? t("automationRuleTable.tooltipOnSchedule")
      : t("automationRuleTable.tooltipOffSchedule");
  }
  return isOn
    ? t("automationRuleTable.tooltipOnEvent")
    : t("automationRuleTable.tooltipOffEvent");
}

const AutomationRuleTable = ({
  automationRules = [],
  rowCount,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditRule,
  onToggleActive,
  onDeleteRule,
  onDeleteSelected,
  isLoading,
}) => {
  const { t } = useTranslation("settings");
  const rows = automationRules.map((rule) => {
    const ativa = Number(rule.ativa ?? 1) !== 0;
    const pausa_motivo = rule.pausa_motivo || "";
    const situacao = !ativa && pausa_motivo
      ? t("automationRuleTable.paused")
      : ativa
        ? t("automationRuleTable.on")
        : t("automationRuleTable.off");
    return {
      id: rule.id,
      descricao: rule.descricao || t("shared.notApplicable"),
      situacao,
      ativa,
      pausa_motivo,
      schedule_type: rule.schedule_type || "",
      _raw: rule,
    };
  });

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "descricao", headerName: t("automationRuleTable.colDescription"), width: 300 },
    {
      field: "situacao",
      headerName: t("automationRuleTable.colStatus"),
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const isOn = Boolean(params.row.ativa);
        const isPaused = !isOn && Boolean(params.row.pausa_motivo);
        const isScheduled = isScheduledRule(params.row);
        const label = isPaused
          ? t("automationRuleTable.paused")
          : isOn
            ? t("automationRuleTable.on")
            : t("automationRuleTable.off");
        return (
          <Tooltip
            title={statusTooltip(t, { isOn, isPaused, isScheduled })}
            enterDelay={300}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={0.75}
              onClick={(event) => event.stopPropagation()}
              sx={{ height: "100%" }}
            >
              <Switch
                size="small"
                checked={isOn}
                disabled={isLoading}
                onChange={(event) =>
                  onToggleActive?.(params.row.id, event.target.checked)
                }
                inputProps={{ "aria-label": t("automationRuleTable.toggleAria") }}
                sx={switchSx}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isPaused ? "warning.main" : isOn ? "success.main" : "error.main",
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {label}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: t("shared.actions"),
      width: 120,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title={t("shared.edit")}>
            <IconButton
              size="small"
              onClick={() => onEditRule?.(params.row._raw)}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("shared.delete")}>
            <IconButton
              size="small"
              onClick={() => onDeleteRule?.(params.row.id)}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle={t("automationRuleTable.exportTitle")}
      exportFileName="regras_automacao"
      onDeleteSelected={onDeleteSelected}
    />
  );
};

export default AutomationRuleTable;
